import { useState, useRef, useEffect } from 'react';
import { Brain, X, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOPIC_KEYS = [
  { key: 'trauma',        labelKey: 'mentor_topicTrauma',        icon: '💔' },
  { key: 'lies',          labelKey: 'mentor_topicLie',           icon: '🎭' },
  { key: 'truth',         labelKey: 'mentor_topicTruth',         icon: '✨' },
  { key: 'wants',         labelKey: 'mentor_topicWants',         icon: '🎯' },
  { key: 'needs',         labelKey: 'mentor_topicNeeds',         icon: '💎' },
  { key: 'relationships', labelKey: 'mentor_topicRelationships', icon: '🤝' },
  { key: 'conflict',      labelKey: 'mentor_topicConflict',      icon: '⚔️' },
];

const LEVEL_LABEL_KEYS = {
  0: 'mentor_level_identity',
  1: 'mentor_level_internalBlueprint',
  2: 'mentor_level_worldIntro',
  3: 'mentor_level_pressureCooker',
  4: 'mentor_level_finalReckoning',
};

export default function AIMentor({ isOpen, onClose, projectId, isPro = false, currentLevel = 0 }) {
  const { t, i18n } = useTranslation();
  const [activeTopic, setActiveTopic] = useState('trauma');
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  // One guarded starter for both "panel opened" and "level changed while
  // open". These used to be two effects; opening the panel on a canvas that
  // was scrolled to a non-zero level flips isOpen AND currentLevel in the
  // same render, so both effects fired and sent TWO metered /ai/mentor calls
  // per open. The signature ref dedupes: one session per (project, level)
  // while open. Fresh opens reset to the default topic; a level change while
  // already open preserves the active topic (matching the old behaviour).
  const startedSigRef = useRef(null);
  useEffect(() => {
    if (!(isOpen && isPro && projectId)) {
      startedSigRef.current = null; // closed — next open starts fresh
      return;
    }
    const sig = `${projectId}:${currentLevel}`;
    if (startedSigRef.current === sig) return; // already started for this open+level
    const isFreshOpen = startedSigRef.current === null;
    startedSigRef.current = sig;
    setHistory([]);
    setInput('');
    const topic = isFreshOpen ? 'trauma' : activeTopic;
    if (isFreshOpen) setActiveTopic('trauma');
    startSession(topic, [], currentLevel);
  }, [isOpen, currentLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const startSession = async (topic, existingHistory, level) => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/ai/mentor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: projectId,
          topic,
          current_level: level,
          history: existingHistory,
          language: i18n.language,
        }),
      });
      console.log('[AIMentor] startSession status:', res.status);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        console.error('[AIMentor] error detail:', errData);
        toast.error(errData.detail || `AI Mentor error (${res.status})`);
        return;
      }
      const data = await res.json();
      setHistory([{ role: 'assistant', content: data.reply }]);
    } catch (err) {
      console.error('[AIMentor] network error:', err);
      toast.error('AI Mentor unavailable — check your network or backend.');
    } finally {
      setLoading(false);
    }
  };

  const handleTopicSwitch = (topic) => {
    setActiveTopic(topic);
    setInput('');
    setHistory([]);
    startSession(topic, [], currentLevel);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');
    const newHistory = [...history, { role: 'user', content: userMessage }];
    setHistory(newHistory);
    setLoading(true);
    try {
      const res = await authFetch(`${API}/ai/mentor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: projectId,
          topic: activeTopic,
          current_level: currentLevel,
          message: userMessage,
          history: history,
          language: i18n.language,
        }),
      });
      console.log('[AIMentor] handleSend status:', res.status);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        console.error('[AIMentor] error detail:', errData);
        toast.error(errData.detail || `AI Mentor error (${res.status})`);
        return;
      }
      const data = await res.json();
      setHistory([...newHistory, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      console.error('[AIMentor] network error:', err);
      toast.error('Failed to get AI response');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (!isPro) {
    return (
      <div className="fixed right-0 top-0 h-full w-80 bg-card border-l border-primary/30 p-6 z-50 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            <h3 className="font-mono text-lg font-semibold">{t('aiMentor')}</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="text-center py-12">
          <Brain className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">{t('mentor_proFeature')}</p>
          <Button
            onClick={() => window.location.href = '/upgrade'}
            className="bg-primary text-primary-foreground rounded-none font-mono text-xs"
          >
            {t('mentor_upgradeBtn')}
          </Button>
        </div>
      </div>
    );
  }

  const currentTopic = TOPIC_KEYS.find(tp => tp.key === activeTopic);

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-card border-l border-primary/30 z-50 shadow-xl flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border/40 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-mono text-sm font-semibold leading-tight">{t('aiMentor')}</h3>
            <p className="text-[10px] font-mono text-primary/70 uppercase tracking-wider">
              Lv{currentLevel} · {t(LEVEL_LABEL_KEYS[currentLevel] || `mentor_level_identity`)}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Topic Selector */}
      <div className="p-3 border-b border-border/40 overflow-x-auto flex-shrink-0">
        <div className="flex gap-1.5">
          {TOPIC_KEYS.map((topic) => (
            <button
              key={topic.key}
              onClick={() => handleTopicSwitch(topic.key)}
              disabled={loading}
              className={`px-2.5 py-1.5 font-mono text-xs whitespace-nowrap transition-all disabled:opacity-50 ${
                activeTopic === topic.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:text-foreground'
              }`}
            >
              {topic.icon} {t(topic.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {history.length === 0 && loading && (
          <div className="flex items-center gap-2 text-muted-foreground text-sm font-mono">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{t('mentor_thinking')}</span>
          </div>
        )}
        {history.map((msg, i) => (
          <div
            key={i}
            className={`${
              msg.role === 'assistant'
                ? 'bg-primary/10 border border-primary/30 p-4 text-sm font-mono leading-relaxed'
                : 'bg-secondary/40 p-3 text-sm text-right ml-8 font-mono leading-relaxed'
            }`}
          >
            {msg.role === 'assistant' && (
              <div className="text-xs text-primary uppercase tracking-wider mb-2 font-mono">
                {currentTopic?.icon} {t(currentTopic?.labelKey || '')}
              </div>
            )}
            {msg.content}
          </div>
        ))}
        {loading && history.length > 0 && (
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-mono">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>{t('mentor_thinking')}</span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-border/40 flex-shrink-0">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder={t('mentor_replyPh')}
            disabled={loading}
            className="flex-1 bg-background border border-input px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
          />
          <Button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="bg-primary text-primary-foreground rounded-none px-3"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
